// ========== PAYMENT-LINK LEAD LOOKUP (standalone — safe to remove) ==========
// When an admin sends a visitor's first proactive message while they're on
// a Shopify "payment link" product page, look up any already-known lead
// info (name/email/phone/details) for that specific product via the
// backend, so it shows up on the admin dashboard. This is entirely
// fire-and-forget: any failure here (wrong page, metafield not found,
// network error) is swallowed silently and can never affect the chat
// itself.
//
// This file is deliberately kept separate from chatbot-scripts.js so the
// feature can be added or removed independently:
//   - To ADD: include <script src="{{ 'payment-link-lookup.js' | asset_url }}" defer></script>
//     (or the equivalent local path) AFTER chatbot-scripts.js on the page.
//   - To REMOVE: delete this file and its <script> tag. chatbot-scripts.js
//     calls into it only through a guarded `window.PaymentLinkLookup &&`
//     check, so nothing else needs to change.
//
// Requires chatbot-scripts.js to have already run on the page (it reads the
// `sessionId`, `PROJECT_NAME`, and `API_BASE_URL` globals it defines).
(function () {
  // Dedup key: one lookup call per distinct product/payment-link page per
  // session — not "once per page view" (an in-memory flag would reset on
  // every navigation, and a visitor commonly gets an admin proactive message
  // on more than one product page in the same session). Persisted in
  // localStorage under the session's own key so it survives navigating away
  // and back; a plain in-memory flag would be lost the moment the page
  // reloads for the next product.
  function getSentLinksStorageKey() {
    return `chatbot_payment_link_lookup_sent__${sessionId}`;
  }

  function hasAlreadyTriggeredForLink(normalizedLink) {
    try {
      const raw = localStorage.getItem(getSentLinksStorageKey());
      const sent = raw ? JSON.parse(raw) : [];
      return Array.isArray(sent) && sent.indexOf(normalizedLink) !== -1;
    } catch (e) {
      return false;
    }
  }

  function markTriggeredForLink(normalizedLink) {
    try {
      const raw = localStorage.getItem(getSentLinksStorageKey());
      const sent = raw ? JSON.parse(raw) : [];
      if (Array.isArray(sent) && sent.indexOf(normalizedLink) === -1) {
        sent.push(normalizedLink);
        localStorage.setItem(getSentLinksStorageKey(), JSON.stringify(sent));
      }
    } catch (e) {
      // Ignore storage failures (private browsing, quota exceeded, etc.) —
      // worst case is an extra duplicate lookup call, which is harmless.
    }
  }

  function isPaymentLinkPage() {
    try {
      return /\/products\//i.test(window.location.pathname);
    } catch (e) {
      return false;
    }
  }

  // Backend/n8n matches on this as a plain "domain/path" string (no scheme,
  // no "www.", no query string) — e.g. "signmakerz.com/products/some-sign" —
  // so query params like ?_pos=6&_sid=... (added by Shopify's own on-site
  // search/recommendation tracking) never cause an otherwise-identical page
  // to fail to match.
  function buildNormalizedPaymentLink() {
    try {
      const host = window.location.hostname.replace(/^www\./i, "");
      return `${host}${window.location.pathname}`;
    } catch (e) {
      return window.location.href;
    }
  }

  // Confirmed (2026-09-21): the product template renders the
  // `custom.calculated_sign_types` metafield via Liquid, but only inside the
  // theme's own page-local function — it isn't exposed as a stable global,
  // and that function's name isn't ours to rely on. custom-chatbot.liquid
  // now carries its own small Liquid block (see snippets/custom-chatbot.liquid
  // in each production theme) that independently reads the same metafield
  // and assigns it to `window.__paymentLinkMetafield`, which is what we read
  // here. That block is optional/removable on its own — if it's missing
  // (e.g. an older snippet build), this falls back to scanning inline
  // <script> tags for the literal JSON text instead.
  function extractAirtableRecordId() {
    try {
      if (window.__paymentLinkMetafield && window.__paymentLinkMetafield.airtable_record_id) {
        return window.__paymentLinkMetafield.airtable_record_id;
      }
      // Cheap checks next, in case a theme update ever exposes it some other way.
      if (window.calculatedSignTypes && window.calculatedSignTypes.airtable_record_id) {
        return window.calculatedSignTypes.airtable_record_id;
      }
      if (window.CALCULATED_SIGN_TYPES && window.CALCULATED_SIGN_TYPES.airtable_record_id) {
        return window.CALCULATED_SIGN_TYPES.airtable_record_id;
      }

      const metafieldEl =
        document.getElementById("calculated-sign-types-metafield") ||
        document.querySelector("[data-calculated-sign-types]") ||
        document.querySelector('script[data-metafield="calculated_sign_types"]');
      if (metafieldEl) {
        const raw =
          metafieldEl.textContent?.trim() ||
          metafieldEl.getAttribute("data-calculated-sign-types");
        if (raw) {
          const parsed = JSON.parse(raw);
          if (parsed && parsed.airtable_record_id) {
            return parsed.airtable_record_id;
          }
        }
      }

      // Primary method for this theme: scan inline scripts for the literal
      // JSON Liquid rendered onto the page.
      const inlineScripts = document.querySelectorAll("script:not([src])");
      for (const script of inlineScripts) {
        const text = script.textContent;
        if (!text || text.indexOf("airtable_record_id") === -1) continue;
        const match = text.match(/["']?airtable_record_id["']?\s*:\s*["']([^"']+)["']/);
        if (match && match[1]) {
          return match[1];
        }
      }
    } catch (e) {
      console.warn("⚠️ Could not read calculated_sign_types metafield:", e);
    }
    return null;
  }

  function triggerPaymentLinkLookup() {
    try {
      if (!isPaymentLinkPage()) return;
      if (typeof sessionId === "undefined" || typeof API_BASE_URL === "undefined") return;

      const normalizedLink = buildNormalizedPaymentLink();
      if (hasAlreadyTriggeredForLink(normalizedLink)) return; // one call per product/payment-link per session

      // If the product's metafield isn't found on this page, still fire the
      // lookup with a blank airtable_record_id — the backend/n8n falls back
      // to matching on payment_link (the current page URL) alone in that case.
      const airtableRecordId = extractAirtableRecordId() || "";

      markTriggeredForLink(normalizedLink);

      fetch(`${API_BASE_URL}/api/payment-link-lookup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: sessionId,
          project_name: typeof PROJECT_NAME !== "undefined" ? PROJECT_NAME : "",
          payment_link: normalizedLink,
          airtable_record_id: airtableRecordId,
        }),
      }).catch((error) => {
        // Fire-and-forget — never surface an error to the visitor for this.
        console.warn("⚠️ Payment-link lead lookup request failed:", error);
      });
    } catch (error) {
      console.warn("⚠️ Payment-link lead lookup failed to run:", error);
    }
  }

  window.PaymentLinkLookup = {
    trigger: triggerPaymentLinkLookup,
  };
})();
